import React, { useState, useEffect } from 'react';
import { Box, Text, VStack, Button, Flex, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaTv, FaUserFriends } from 'react-icons/fa';
import { SiJsonwebtokens } from 'react-icons/si';
import { getJobCirculars } from '../../../services/jobCircularsService';
import { getLocations } from '../../../services/locationsService';

const jobIcons = [
  <FaTv size={48} color="#8B5CF6" />,
  <SiJsonwebtokens size={48} color="#059669" />,
  <FaUserFriends size={48} color="#EC4899" />,
];

const SimilarJobPosts = ({ currentJobId, limit = 3 }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSimilarJobs = async () => {
      try {
        setLoading(true);
        
        // Fetch locations first for proper mapping
        const locationsResult = await getLocations();
        const locations = locationsResult.success ? locationsResult.locations : [];
        
        // Fetch all jobs
        const jobsResult = await getJobCirculars(locations);
        
        if (jobsResult.success) {
          // Filter out current job and expired jobs, then limit results
          const currentDate = new Date();
          const similarJobs = jobsResult.jobs
            .filter(job => {
              const jobDeadline = job.deadline ? new Date(job.deadline) : null;
              const isNotExpired = !jobDeadline || jobDeadline >= currentDate;
              return job.id !== currentJobId && isNotExpired; // Only show active jobs
            })
            .slice(0, limit);
          
          setJobs(similarJobs);
          setError(null);
        } else {
          setError(jobsResult.error || 'Failed to fetch similar jobs');
        }
      } catch (err) {
        setError('Failed to fetch similar jobs: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarJobs();
  }, [currentJobId, limit]);

  const handleJobDetails = (job) => {
    navigate(`/job-details/${job.encoded_id}`, {
      state: { job }
    });
  };

  if (loading) {
    return (
      <Box mt={16} px={{ base: 2, md: 8, lg: 32 }}>
        <Text fontWeight="bold" fontSize={{ base: 'xl', md: '3xl' }} color="#234F4E" textAlign="center" mb={6}>
          Similar Job Post
        </Text>
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" color="#234F4E" />
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={16} px={{ base: 2, md: 8, lg: 32 }}>
        <Text fontWeight="bold" fontSize={{ base: 'xl', md: '3xl' }} color="#234F4E" textAlign="center" mb={6}>
          Similar Job Post
        </Text>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!jobs.length) {
    return (
      <Box mt={16} px={{ base: 2, md: 8, lg: 32 }}>
        <Text fontWeight="bold" fontSize={{ base: 'xl', md: '3xl' }} color="#234F4E" textAlign="center" mb={6}>
          Similar Job Post
        </Text>
        <Text textAlign="center" color="gray.500" py={8}>
          No similar jobs found at the moment.
        </Text>
      </Box>
    );
  }

  return (
    <Box mt={16} px={{ base: 2, md: 8, lg: 32 }}>
      <Text fontWeight="bold" fontSize={{ base: 'xl', md: '3xl' }} color="#234F4E" textAlign="center" mb={6}>
        Similar Job Post
      </Text>
      <VStack spacing={{ base: 3, md: 4 }}>
        {jobs.map((job, idx) => (
          <Flex
            key={job.id}
            bg="white"
            borderRadius="2xl"
            border="1px solid #E3EAF2"
            boxShadow="sm"
            p={{ base: 3, md: 6 }}
            w="100%"
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent={{ base: 'flex-start', md: 'space-between' }}
            gap={{ base: 3, md: 4 }}
            direction={{ base: 'column', md: 'row' }}
          >
            <Flex align="center" gap={3} minW={0} w={{ base: '100%', md: 'auto' }}>
              <Box>{jobIcons[idx % jobIcons.length]}</Box>
              <Box minW={0}>
                <Text color="green.500" fontWeight="bold" fontSize={{ base: 'md', md: 'lg' }} mb={1}>
                  {job.type || 'Fulltime'}
                </Text>
                <Text fontWeight="bold" fontSize={{ base: 'md', md: 'xl' }} mb={1} noOfLines={1}>
                  {job.title}
                </Text>
              </Box>
            </Flex>
            <Box textAlign={{ base: 'left', md: 'right' }} minW={{ base: 'auto', md: '180px' }} w={{ base: '100%', md: 'auto' }}>
              <Text color="gray.500" fontSize={{ base: 'sm', md: 'md' }}>{job.location}</Text>
              <Text color="gray.400" fontSize={{ base: 'sm', md: 'md' }}>
                {job.salary === 'Negotiable' || !job.salary ? 'Negotiable' : `${job.salary} BDT`}
              </Text>
            </Box>
            <Button 
              bg="#234F4E" 
              color="#E6FF3F" 
              borderRadius="full" 
              fontWeight="bold" 
              px={8} 
              w={{ base: '100%', md: 'auto' }}
              mt={{ base: 2, md: 0 }}
              _hover={{ bg: '#1B3A4B' }}
              onClick={() => handleJobDetails(job)}
            >
              Details
            </Button>
          </Flex>
        ))}
      </VStack>
      <Flex justify="center" align="center" mt={8} gap={4} direction={{ base: 'column', sm: 'row' }}>
        <Button 
          variant="outline" 
          color="#234F4E" 
          borderColor="#D1D5DB" 
          borderRadius="full" 
          px={8} 
          fontWeight="bold" 
          bg="white"
          w={{ base: '100%', sm: 'auto' }}
          mb={{ base: 2, sm: 0 }}
          onClick={() => navigate('/job-opening')}
        >
          See more
        </Button>
        <Button 
          bg="#234F4E" 
          color="white" 
          borderRadius="full" 
          minW={10} 
          minH={10} 
          p={0} 
          _hover={{ bg: '#1B3A4B' }}
          w={{ base: '100%', sm: 'auto' }}
          onClick={() => navigate('/job-opening')}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Button>
      </Flex>
    </Box>
  );
};

export default SimilarJobPosts; 